import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { collection, deleteDoc, doc, getDocs, setDoc, updateDoc } from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { db } from 'config/firebase'
import { GridApiPro, GridColumnDimensions, GridColumnVisibilityModel, GridInitialState, GridPinnedColumnFields } from '@mui/x-data-grid-pro'
import { useAuth } from 'contexts/AuthContext'
import { updateUser } from 'core/users'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import {
  DEFAULT_V3_GRID_STATE,
  DEFAULT_V3_TEMPLATE_ID,
  ensureCheckboxPinnedLeft,
  mergeColumnDimensions,
  mergeColumnOrder,
} from './inventoryV3DefaultTemplate'

const COLLECTION_NAME = 'column_templates_v3'
const QUERY_KEY = ['inventoryV3ColumnTemplates']

export interface InventoryV3Template {
  id: string
  name: string
  description?: string
  /** MUI DataGrid state produced by apiRef.current.exportState(). */
  gridState: GridInitialState
  createdBy: string
  createdAt: string
  updatedBy?: string
  updatedAt?: string
}

interface UseInventoryV3TemplatesParams {
  apiRef: React.RefObject<GridApiPro>
  columnVisibilityModel: GridColumnVisibilityModel
  columnDimensions: Record<string, GridColumnDimensions>
  orderedFields: string[]
  pinnedColumns: GridPinnedColumnFields
}

/** Firestore rejects `undefined`; exportState leaves plenty of it behind. */
const stripUndefined = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T

/** Firestore field names cannot begin and end with `__` (e.g. `__check__`). */
const RESERVED_FIELD_KEY = /^__.*__$/

const omitReservedKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(omitReservedKeys)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key]) => !RESERVED_FIELD_KEY.test(key))
        .map(([key, nested]) => [key, omitReservedKeys(nested)]),
    )
  }
  return value
}

const sanitizeGridState = (state: GridInitialState): GridInitialState =>
  omitReservedKeys(stripUndefined(state)) as GridInitialState

const captureGridState = (
  api: GridApiPro,
  columnVisibilityModel: GridColumnVisibilityModel,
  columnDimensions: Record<string, GridColumnDimensions>,
  orderedFields: string[],
  pinnedColumns: GridPinnedColumnFields,
): GridInitialState => {
  const exported = api.exportState()
  return sanitizeGridState({
    ...exported,
    pinnedColumns: ensureCheckboxPinnedLeft(pinnedColumns),
    columns: {
      ...exported.columns,
      columnVisibilityModel,
      dimensions: columnDimensions,
      orderedFields,
    },
  })
}

/** Kept in memory only — never written to Firestore. */
const withInternalColumnVisibility = (state: GridInitialState): GridInitialState => ({
  ...state,
  columns: {
    ...state.columns,
    columnVisibilityModel: {
      __detail_panel_toggle__: false,
      ...state.columns?.columnVisibilityModel,
    },
  },
})

export const useInventoryV3Templates = ({
  apiRef,
  columnVisibilityModel,
  columnDimensions,
  orderedFields,
  pinnedColumns,
}: UseInventoryV3TemplatesParams) => {
  const queryClient = useQueryClient()
  const auth = getAuth()
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(DEFAULT_V3_TEMPLATE_ID)
  const [optimisticFavoriteId, setOptimisticFavoriteId] = useState(
    () => userInfo?.default_view_v3 || DEFAULT_V3_TEMPLATE_ID,
  )
  const favoriteTemplateId = optimisticFavoriteId || DEFAULT_V3_TEMPLATE_ID

  useEffect(() => {
    if (userInfo?.default_view_v3) setOptimisticFavoriteId(userInfo.default_view_v3)
  }, [userInfo?.default_view_v3])

  const updateFavoriteMutation = useMutation({
    mutationFn: async (templateId: string) => {
      if (!userInfo?.id) throw new Error('User not authenticated')
      const saved = await updateUser(userInfo.id, { default_view_v3: templateId })
      if (!saved) throw new Error('Could not save favorite template')
      return templateId
    },
    onMutate: templateId => {
      const previous = optimisticFavoriteId
      setOptimisticFavoriteId(templateId)
      return { previous }
    },
    onError: (_error, _templateId, context) => {
      if (context?.previous) setOptimisticFavoriteId(context.previous)
      showError('Could not save favorite template')
    },
  })

  const didMigrateLocalFavorite = useRef(false)
  useEffect(() => {
    if (didMigrateLocalFavorite.current || !userInfo?.id || userInfo.default_view_v3) return
    const legacy =
      window.localStorage.getItem('master_inventory-inventory-v3-favorite-template') ||
      window.localStorage.getItem('shield_inventory-inventory-v3-favorite-template')
    if (!legacy || legacy === DEFAULT_V3_TEMPLATE_ID) return
    didMigrateLocalFavorite.current = true
    updateFavoriteMutation.mutate(legacy)
  }, [updateFavoriteMutation, userInfo?.default_view_v3, userInfo?.id])

  const setFavoriteTemplate = useCallback(
    (templateId: string) => {
      if (templateId === favoriteTemplateId) return
      updateFavoriteMutation.mutate(templateId)
    },
    [favoriteTemplateId, updateFavoriteMutation],
  )

  const { data: templates = [], isLoading: isLoadingTemplates } = useQuery<InventoryV3Template[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME))
      return snapshot.docs.map(document => ({ id: document.id, ...document.data() }) as InventoryV3Template)
    },
    staleTime: 5 * 60 * 1000,
  })

  const saveTemplateMutation = useMutation({
    mutationFn: async ({ name, description }: { name: string; description?: string }) => {
      if (!auth.currentUser?.email) throw new Error('User not authenticated')
      if (!apiRef.current?.exportState) throw new Error('Grid is not ready')
      const gridState = captureGridState(
        apiRef.current,
        columnVisibilityModel,
        columnDimensions,
        orderedFields,
        pinnedColumns,
      )
      const docRef = doc(collection(db, COLLECTION_NAME))
      await setDoc(docRef, {
        name,
        ...(description ? { description } : {}),
        gridState,
        createdBy: auth.currentUser.email,
        createdAt: new Date().toISOString(),
      })
      return docRef.id
    },
    onSuccess: id => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      setActiveTemplateId(id)
      showSuccess('Template saved')
    },
    onError: () => showError('Could not save template'),
  })

  const updateTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      if (!auth.currentUser?.email) throw new Error('User not authenticated')
      if (!apiRef.current?.exportState) throw new Error('Grid is not ready')
      const gridState = captureGridState(
        apiRef.current,
        columnVisibilityModel,
        columnDimensions,
        orderedFields,
        pinnedColumns,
      )
      await updateDoc(doc(db, COLLECTION_NAME, templateId), {
        gridState,
        updatedBy: auth.currentUser.email,
        updatedAt: new Date().toISOString(),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      showSuccess('Template updated')
    },
    onError: () => showError('Could not update template'),
  })

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      await deleteDoc(doc(db, COLLECTION_NAME, templateId))
      return templateId
    },
    onSuccess: templateId => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY })
      setActiveTemplateId(current => (current === templateId ? DEFAULT_V3_TEMPLATE_ID : current))
      if (favoriteTemplateId === templateId) updateFavoriteMutation.mutate(DEFAULT_V3_TEMPLATE_ID)
      showSuccess('Template deleted')
    },
    onError: () => showError('Could not delete template'),
  })

  /**
   * Returns the state that was restored so the caller can resync any controlled
   * grid models (visibility, pinning) that `restoreState` does not push back out.
   */
  const applyTemplate = useCallback(
    (templateId: string): GridInitialState | null => {
      if (!apiRef.current?.restoreState) return null
      if (templateId === DEFAULT_V3_TEMPLATE_ID || !templateId) {
        apiRef.current.restoreState(DEFAULT_V3_GRID_STATE)
        setActiveTemplateId(DEFAULT_V3_TEMPLATE_ID)
        return DEFAULT_V3_GRID_STATE
      }
      const template = templates.find(candidate => candidate.id === templateId)
      if (!template) return null
      const gridState = withInternalColumnVisibility(template.gridState)
      apiRef.current.restoreState(gridState)
      setActiveTemplateId(templateId)
      return {
        ...gridState,
        pinnedColumns: ensureCheckboxPinnedLeft(gridState.pinnedColumns ?? { left: [], right: [] }),
        columns: {
          ...gridState.columns,
          dimensions: mergeColumnDimensions(gridState.columns?.dimensions),
          orderedFields: mergeColumnOrder(gridState.columns?.orderedFields),
        },
      }
    },
    [apiRef, templates],
  )

  return {
    templates,
    isLoadingTemplates,
    activeTemplateId,
    favoriteTemplateId,
    setFavoriteTemplate,
    applyTemplate,
    saveTemplate: saveTemplateMutation.mutateAsync,
    updateTemplate: updateTemplateMutation.mutateAsync,
    deleteTemplate: deleteTemplateMutation.mutateAsync,
    isSavingTemplate: saveTemplateMutation.isPending || updateTemplateMutation.isPending,
  }
}
